/**
 * Module dependencies.
 */

import { Svg } from '@untile/react-core/components/svg';
import { ReactNode, forwardRef, useMemo } from 'react';
import ReactSelect, {
  ClearIndicatorProps,
  ControlProps,
  DropdownIndicatorProps,
  LoadingIndicatorProps,
  OptionProps,
  Props as ReactSelectProps,
  components,
  createFilter
} from 'react-select';
import clearIcon from 'src/assets/24/cancel.svg';
import caretIcon from 'src/assets/24/chevron-down.svg';
import { VirtualizedMenuList } from 'src/components/core/forms/select/menu-list';
import { ErrorMessage, FloatingLabel, HelpText } from 'src/components/core/forms/styles';
import { Spinner } from 'src/components/core/spinner';
import { useBreakpoint } from 'src/hooks/use-breakpoints';
import styled from 'styled-components';
import { ifProp } from 'styled-tools';

import { EmptyResultsOption } from './empty-results-option';

/**
 * Export `Option` type.
 */

export type Option = {
  description?: ReactNode;
  disabled?: boolean;
  label: string;
  value?: string;
};

/**
 * Export `RenderOptionProps` type.
 */

export type RenderOptionProps = {
  isDisabled?: boolean;
  isSelected: boolean;
  option: Omit<Option, 'disabled'>;
};

/**
 * Export `SelectProps` type.
 */

export type SelectProps = {
  className?: string;
  disabled?: boolean;
  error?: string;
  helpText?: string;
  label: string;
  noOptionsMessage?: string;
  onChange: (value: Option['value']) => void;
  options: Option[];
  renderOption?: (props: RenderOptionProps) => ReactNode;
} & Omit<ReactSelectProps, 'isDisabled' | 'noOptionsMessage' | 'onChange'>;

/**
 * `StyledReactSelect` styled component.
 */

const StyledReactSelect = styled(ReactSelect)<{
  hasCustomOptionRender: boolean;
}>`
  .react-select {
    &__control {
      background-color: var(--color-white);
      border: 1px solid var(--color-grey400);
      border-radius: 8px;
      box-shadow: none;
      display: grid;
      grid-template-areas: 'label icon' 'input icon';
      grid-template-columns: 1fr min-content;
      min-height: 64px;
      padding: 0 0 8px;
      position: relative;

      &:focus,
      &:hover {
        border-color: var(--color-blue400);
      }

      &--is-focused {
        border-color: var(--color-blue400);
      }

      &--menu-is-open {
        border-color: var(--color-blue500);
      }
    }

    &__label {
      grid-area: label;
    }

    &__value-container {
      grid-area: input;
      padding: 0;
    }

    &__indicators {
      color: var(--color-grey500);
      grid-area: icon;
      margin-right: 8px;
      margin-top: 8px;
    }

    &__single-value,
    &__input-container {
      margin: 0;
      padding: 0 16px;
    }

    &__menu {
      border: 1px solid var(--color-blue500);
      border-radius: 8px;
      box-shadow: none;
      margin: 8px 0;
      padding: 4px;
      z-index: var(--z-index-dropdown);

      &-list {
        display: grid;
        grid-gap: 4px;
        grid-template-columns: 1fr;
        padding: 0;
      }
    }

    &__option {
      background-color: var(--color-white);
      border-radius: 4px;
      cursor: pointer;
      margin-bottom: ${ifProp('hasCustomOptionRender', '8px', 0)};
      padding: ${ifProp('hasCustomOptionRender', 0, '8px 0')};
      position: relative;
      transition: var(--transition-default);
      transition-property: background-color, color;
      width: 100%;

      &:focus,
      &:hover {
        background-color: var(--color-grey100);
      }

      &--is-focused {
        background-color: var(--color-grey100);
      }

      &--is-selected {
        background-color: var(--color-blue100);
        color: var(--color-grey900);
        cursor: default;
        pointer-events: none;
      }
    }
  }
`;

/**
 * `UnstyledButton` styled component.
 */

const UnstyledButton = styled.button`
  background: none;
  border: none;
  color: currentcolor;
  line-height: 0;
  padding: 0;
`;

/**
 * `Control` component.
 */

function Control(props: ControlProps & Pick<SelectProps, 'label'>) {
  const { children, label, ...rest } = props;

  return (
    <components.Control {...rest}>
      <FloatingLabel className={'react-select__label'} isFloating={rest?.hasValue || rest?.isFocused}>
        {label}
      </FloatingLabel>

      {children}
    </components.Control>
  );
}

/**
 * `DropdownIndicator` component.
 */

function DropdownIndicator({ innerProps }: DropdownIndicatorProps) {
  return (
    <span {...innerProps} style={{ lineHeight: 0 }}>
      <Svg icon={caretIcon} size={'24px'} />
    </span>
  );
}

/**
 * `ClearIndicator` component.
 */

function ClearIndicator({ innerProps }: ClearIndicatorProps) {
  return (
    <UnstyledButton {...(innerProps as any)}>
      <Svg icon={clearIcon} size={'24px'} />
    </UnstyledButton>
  );
}

/**
 * `LoadingIndicator` component.
 */

function LoadingIndicator({ innerProps }: LoadingIndicatorProps) {
  return <Spinner {...(innerProps as any)} isActive isRelative />;
}

/**
 * `Option` component.
 */

const Option = (props: OptionProps & Required<Pick<SelectProps, 'renderOption'>>) => {
  const { renderOption, ...rest } = props;

  return (
    <components.Option {...rest}>
      {renderOption({
        isDisabled: rest?.isDisabled,
        isSelected: rest?.isSelected,
        option: rest?.data as Option
      })}
    </components.Option>
  );
};

/**
 * `defaultRenderOption` utils.
 */

function defaultRenderOption({ option }: RenderOptionProps) {
  return <div style={{ padding: '0 16px' }}>{option.label}</div>;
}

/**
 * Export `Select` component.
 */

export const Select = forwardRef((props: SelectProps, ref: any) => {
  const { disabled, error, helpText, label, noOptionsMessage, onChange, options, renderOption, value, ...rest } = props;

  const isMobile = useBreakpoint(0, 'md');
  const selectedOption = useMemo(() => {
    return options.find((option: any) => option.value === value) as Option;
  }, [options, value]);

  const componentsConfig = useMemo(() => {
    const isLargeList = options.length < 30;

    return {
      autoComplete: !isMobile || isLargeList,
      ...(isLargeList ? undefined : { MenuList: VirtualizedMenuList }),
      ClearIndicator,
      Control: (props: any) => <Control {...props} label={label} />,
      DropdownIndicator,
      IndicatorSeparator: null,
      LoadingIndicator,
      NoOptionsMessage: () => <EmptyResultsOption message={noOptionsMessage} />,
      Option: (props: any) => <Option {...props} renderOption={renderOption ?? defaultRenderOption} />
    };
  }, [isMobile, label, noOptionsMessage, options.length, renderOption]);

  return (
    <div>
      <StyledReactSelect
        {...rest}
        captureMenuScroll={false}
        classNamePrefix={'react-select'}
        components={componentsConfig as any}
        filterOption={createFilter({ ignoreAccents: false })}
        hasCustomOptionRender={!!renderOption}
        isDisabled={disabled}
        onChange={(option: any) => {
          onChange(option?.value);
        }}
        options={options}
        placeholder={null}
        ref={ref}
        value={selectedOption}
        {...(rest.name && {
          instanceId: rest.name
        })}
      />

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {!error && helpText && <HelpText>{helpText}</HelpText>}
    </div>
  );
});

/**
 * `Select` display name.
 */

Select.displayName = 'Select';
